import { Link, useNavigate } from "react-router-dom";
import { BarChart3, Camera, Crown, ScanLine, Heart, LogOut, Settings, Wand2 } from "lucide-react";
import { BrandLink } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [["How it works", "#how"], ["Features", "#features"], ["Demo", "#demo"], ["Pricing", "#pricing"]];

const UserMenu = ({ user, onLogout }) => {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger" className="h-10 w-10 rounded-full overflow-hidden border-2 border-primary/40 bg-secondary flex items-center justify-center font-bold text-sm">
        {user.name?.[0]}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">{user.email}</div>
        <DropdownMenuItem data-testid="menu-open-app" onClick={() => navigate("/app")}><Wand2 className="h-4 w-4 mr-2" />Reply generator</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-photo-rizz" onClick={() => navigate("/photo-rizz")}><Camera className="h-4 w-4 mr-2" />Photo Rizz</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-upgrade" onClick={() => navigate("/upgrade")}><Crown className="h-4 w-4 mr-2" />Plans & usage</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-favorites" onClick={() => navigate("/favorites")}><Heart className="h-4 w-4 mr-2" />Favourites</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}><Settings className="h-4 w-4 mr-2" />Settings & privacy</DropdownMenuItem>
        {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><BarChart3 className="h-4 w-4 mr-2" />Usage dashboard</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={onLogout}><LogOut className="h-4 w-4 mr-2" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = ({ landing = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => {
    await logout();
    navigate("/");
  };
  return (
    <header className="glass sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <BrandLink />
        {landing && (
          <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
            {NAV.map(([label, href]) => (
              <a key={href} href={href} data-testid={`nav-${href.slice(1)}`} className="hover:text-foreground transition-colors">{label}</a>
            ))}
          </nav>
        )}
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {user ? (
            <>
              {landing && (
                <Link to="/app" data-testid="header-open-app-button" className="hidden sm:inline-flex h-10 items-center rounded-full px-5 text-sm font-semibold brand-gradient text-white hover:opacity-90 transition-opacity">
                  Open app
                </Link>
              )}
              <Link to="/scan" data-testid="header-scan-link" className="inline-flex h-10 items-center gap-1.5 rounded-full px-3 sm:px-4 text-sm font-semibold brand-gradient text-white hover:opacity-90 transition-opacity">
                <ScanLine className="h-4 w-4" /><span className="hidden sm:inline">Scan</span>
              </Link>
              <Link to="/photo-rizz" data-testid="header-photo-rizz-link" className="inline-flex h-10 items-center gap-1.5 rounded-full px-3 sm:px-4 text-sm font-semibold border border-primary/50 hover:bg-primary/10 transition-colors">
                <Camera className="h-4 w-4" /><span className="hidden sm:inline">Photo Rizz</span>
              </Link>
              <UserMenu user={user} onLogout={doLogout} />
            </>
          ) : (
            <button data-testid="header-google-signin-button" onClick={() => navigate("/login")} className="h-10 rounded-full px-5 text-sm font-semibold bg-foreground text-background hover:opacity-90 transition-opacity">
              Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
